import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import {
  ptoKfoApi,
  usePutKfoReportItemsMutation,
  type KfoLinesArgs,
  type KfoReportDetail,
} from '@shared/api';
import { useAppDispatch } from '@app/hooks';
import type { RootState } from '@app/store';
import { errorText, parseQty, qtyToInput } from './ui';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface QtySaver {
  /** Текст поля: несохранённый ввод или значение сервера. */
  value: (lineId: string, serverQty: number | null | undefined) => string;
  /** Ввод не число (или отрицательное) — не отправлен. */
  isInvalid: (lineId: string) => boolean;
  /** Ввод ждёт отправки или в полёте. */
  isPending: (lineId: string) => boolean;
  /** Последняя отправка по строке не прошла. */
  isFailed: (lineId: string) => boolean;
  set: (lineId: string, text: string) => void;
  /**
   * Отправить накопленное немедленно (blur / Enter) и дождаться ВСЕХ отправок
   * по отчёту, включая уже летящую. `true` — всё сохранено; `false` — отправка
   * не прошла (причина — в `error`). Перед «Проверить» / «Выпустить» / XLSX
   * ждать обязательно, иначе сервер увидит позиции без последнего ввода.
   */
  flush: () => Promise<boolean>;
  status: SaveStatus;
  error: string | null;
}

/**
 * Куда сообщить о несохранённом объёме, когда экран, где его вводили, уже
 * закрыт (досылка при смене отчёта или уходе с вкладки). Раздел «КФО»
 * подставляет свой баннер; без него — системное окно: потеря ввода не должна
 * пройти молча.
 */
export const KfoSaveErrorContext = createContext<(message: string) => void>((message) =>
  window.alert(message),
);

/**
 * Отправки по отчёту строго по одной — очередь общая на модуль, а не на
 * экземпляр хука: реестр и карточка отчёта пишут в один черновик, и досылка
 * при уходе с вкладки не должна обогнать ещё летящий PUT (поздний ответ
 * старого ввода перетёр бы новый).
 */
const chains = new Map<string, Promise<void>>();

function enqueue<T>(rid: string, task: () => Promise<T>): Promise<T> {
  const run = (chains.get(rid) ?? Promise.resolve()).then(task);
  const tail = run.then(
    () => undefined,
    () => undefined,
  );
  chains.set(rid, tail);
  void tail.then(() => {
    if (chains.get(rid) === tail) chains.delete(rid);
  });
  return run;
}

/**
 * Ввод объёма периода в черновик отчёта прямо в строке таблицы (как колонка
 * «Физический объем» в книге Заказчика).
 *
 * Правки копятся и уходят одним `PUT /kfo/reports/{id}/items` через
 * `delayMs` после последнего нажатия (или сразу по blur/Enter); пустое поле =
 * 0 = удалить позицию. Отправки по отчёту идут строго по одной (`enqueue`),
 * чтобы ответ на старый ввод не перетёр новый. После ответа сервера объём
 * сразу переписывается в закэшированных страницах реестра этого отчёта
 * (`draft_qty`) и в карточке отчёта — без перечитывания 20 тыс. строк.
 */
export function useQtySaver(reportId: string | null, delayMs = 700): QtySaver {
  const [put] = usePutKfoReportItemsMutation();
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const onLost = useContext(KfoSaveErrorContext);

  const putRef = useRef(put);
  putRef.current = put;
  const lostRef = useRef(onLost);
  lostRef.current = onLost;

  const [edits, setEdits] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState<Record<string, true>>({});
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const queue = useRef(new Map<string, number>());
  const inFlight = useRef(new Set<string>());
  const timer = useRef<number | null>(null);
  /** Отчёт этого экрана; `null` после смены отчёта / ухода — ответы уже «не наши». */
  const reportRef = useRef(reportId);
  /** Итог последней отправки этого экрана — ответ `flush`, когда слать нечего. */
  const lastOk = useRef(true);

  /** Переписать `draft_qty` во всех закэшированных страницах реестра этого отчёта. */
  const patchLinesCache = useCallback(
    (rid: string, detail: KfoReportDetail) => {
      const byLine = new Map(detail.items.map((i) => [i.line_id, i.qty]));
      const entries = ptoKfoApi.util.selectInvalidatedBy(store.getState() as never, ['KfoLines']);
      for (const e of entries) {
        if (e.endpointName !== 'getKfoLines') continue;
        const args = e.originalArgs as KfoLinesArgs;
        if (args.report_id !== rid) continue;
        dispatch(
          ptoKfoApi.util.updateQueryData('getKfoLines', args, (draft) => {
            for (const l of draft.items) l.draft_qty = byLine.get(l.id) ?? null;
          }),
        );
      }
    },
    [dispatch, store],
  );

  /** Ответ PUT — в кэш: реестр и карточка отчёта (PUT её больше не инвалидирует). */
  const applyDetail = useCallback(
    (rid: string, detail: KfoReportDetail) => {
      patchLinesCache(rid, detail);
      void dispatch(ptoKfoApi.util.upsertQueryData('getKfoReport', rid, detail));
    },
    [patchLinesCache, dispatch],
  );

  /** Отправить накопленное к моменту своей очереди (выполняется внутри `enqueue`). */
  const drain = useCallback(
    async (rid: string): Promise<boolean> => {
      // Отчёт сменили, пока ждали очереди: накопленное по старому ушло досылкой
      // при смене, а в `queue` теперь ввод по НОВОМУ отчёту — его сюда не слать.
      if (reportRef.current !== rid) return lastOk.current;
      if (queue.current.size === 0) return lastOk.current;
      const items = Array.from(queue.current, ([line_id, qty]) => ({ line_id, qty }));
      queue.current = new Map();
      items.forEach((i) => inFlight.current.add(i.line_id));
      setStatus('saving');
      try {
        const detail = await putRef.current({ reportId: rid, items }).unwrap();
        applyDetail(rid, detail);
        lastOk.current = true;
        if (reportRef.current === rid) {
          setEdits((prev) => {
            const next = { ...prev };
            for (const i of items) if (!queue.current.has(i.line_id)) delete next[i.line_id];
            return next;
          });
          setFailed((prev) => {
            const next = { ...prev };
            for (const i of items) delete next[i.line_id];
            return next;
          });
          setError(null);
          setStatus('saved');
        }
        return true;
      } catch (e) {
        lastOk.current = false;
        const msg = errorText(e, 'Не удалось сохранить объём. Проверьте значение и повторите.');
        if (reportRef.current === rid) {
          setFailed((prev) => {
            const next = { ...prev };
            for (const i of items) if (!queue.current.has(i.line_id)) next[i.line_id] = true;
            return next;
          });
          setError(msg);
          setStatus('error');
        } else {
          // Экран уже закрыт или показывает другой отчёт — сказать больше некому.
          lostRef.current(`Объём по строкам (${items.length}) не сохранён: ${msg}`);
        }
        return false;
      } finally {
        items.forEach((i) => inFlight.current.delete(i.line_id));
      }
    },
    [applyDetail],
  );

  const flush = useCallback((): Promise<boolean> => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    const rid = reportRef.current;
    if (!rid) return Promise.resolve(true);
    return enqueue(rid, () => drain(rid));
  }, [drain]);

  const flushRef = useRef(flush);
  flushRef.current = flush;

  // Смена отчёта (или уход со страницы): недосланное уходит в СТАРЫЙ отчёт —
  // через ту же очередь, после летящего PUT; локальный ввод сбрасывается.
  useEffect(() => {
    reportRef.current = reportId;
    lastOk.current = true;
    setEdits({});
    setFailed({});
    setError(null);
    setStatus('idle');
    return () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
      const rid = reportRef.current;
      reportRef.current = null;
      if (rid && queue.current.size > 0) {
        const items = Array.from(queue.current, ([line_id, qty]) => ({ line_id, qty }));
        queue.current = new Map();
        void enqueue(rid, async () => {
          try {
            const detail = await putRef.current({ reportId: rid, items }).unwrap();
            applyDetail(rid, detail);
          } catch (e) {
            lostRef.current(
              `Объём по строкам (${items.length}) не сохранён: ${errorText(
                e,
                'сервер не принял ввод. Откройте отчёт и введите объём заново.',
              )}`,
            );
          }
        });
      }
    };
  }, [reportId, applyDetail]);

  const set = useCallback(
    (lineId: string, text: string) => {
      setEdits((prev) => ({ ...prev, [lineId]: text }));
      const q = parseQty(text);
      if (q === null) {
        queue.current.delete(lineId);
        return;
      }
      queue.current.set(lineId, q);
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        void flushRef.current();
      }, delayMs);
    },
    [delayMs],
  );

  return {
    value: (lineId, serverQty) => edits[lineId] ?? qtyToInput(serverQty),
    isInvalid: (lineId) => lineId in edits && parseQty(edits[lineId]) === null,
    isPending: (lineId) => queue.current.has(lineId) || inFlight.current.has(lineId),
    isFailed: (lineId) => !!failed[lineId],
    set,
    flush,
    status,
    error,
  };
}
